/**
 * Which codec a video's picture is in, read from the file itself.
 *
 * Browsers differ here: H.264, VP8, VP9 and AV1 play almost everywhere, but
 * H.265 (HEVC) only where the graphics card decodes it and the browser is
 * allowed to use it. Elsewhere the sound plays and the picture never comes. The
 * upload forms warn before such a video goes up, and the player says why it has
 * no picture.
 */
export type VideoCodec = "h264" | "hevc" | "av1" | "vp9" | "vp8" | "prores" | "other";

export interface VideoCodecInfo {
  codec: VideoCodec;
  /** The four letters in the file ("hvc1", "avc1"…). */
  tag: string;
}

/** Codecs every current browser shows: anything else may play with sound only. */
export const SAFE_VIDEO_CODECS: readonly VideoCodec[] = ["h264", "vp9", "vp8", "av1"];

const TAGS: Record<string, VideoCodec> = {
  avc1: "h264",
  avc3: "h264",
  hvc1: "hevc",
  hev1: "hevc",
  dvh1: "hevc",
  dvhe: "hevc",
  av01: "av1",
  vp09: "vp9",
  vp08: "vp8",
  apch: "prores",
  apcn: "prores",
  apcs: "prores",
  apco: "prores",
  ap4h: "prores",
  ap4x: "prores",
};

/** Reads `length` bytes from `offset` (a file slice, a Range request); may return fewer at the end. */
export type ByteReader = (offset: number, length: number) => Promise<Uint8Array>;

/** The most of the file's index ("moov") we read: a few hundred KB even for long clips. */
const MAX_MOOV_BYTES = 16 * 1024 * 1024;

const ascii = (b: Uint8Array, at: number) => String.fromCharCode(b[at], b[at + 1], b[at + 2], b[at + 3]);
const u32 = (b: Uint8Array, at: number) => ((b[at] << 24) >>> 0) + (b[at + 1] << 16) + (b[at + 2] << 8) + b[at + 3];

interface Box {
  type: string;
  /** Where its content starts and ends, in the buffer or the file. */
  start: number;
  end: number;
}

/** The boxes inside `buf` from `from` to `to` (an MP4 is boxes in boxes). */
function children(buf: Uint8Array, from: number, to: number): Box[] {
  const out: Box[] = [];
  let at = from;
  while (at + 8 <= to && out.length < 512) {
    let size = u32(buf, at);
    const type = ascii(buf, at + 4);
    let head = 8;
    if (size === 1) {
      if (at + 16 > to) break;
      size = u32(buf, at + 8) * 2 ** 32 + u32(buf, at + 12);
      head = 16;
    } else if (size === 0) size = to - at;
    if (size < head || at + size > to) break;
    out.push({ type, start: at + head, end: at + size });
    at += size;
  }
  return out;
}

const child = (buf: Uint8Array, box: Box | undefined, type: string) => (box ? children(buf, box.start, box.end).find((b) => b.type === type) : undefined);

/** The picture's sample format in an MP4 index: moov › trak (a video one) › mdia › minf › stbl › stsd. */
function codecInMoov(moov: Uint8Array): VideoCodecInfo | null {
  for (const trak of children(moov, 0, moov.length).filter((b) => b.type === "trak")) {
    const mdia = child(moov, trak, "mdia");
    const hdlr = child(moov, mdia, "hdlr");
    // hdlr: version and flags (4), pre_defined (4), then the handler: "vide" for pictures.
    if (!hdlr || hdlr.end - hdlr.start < 12 || ascii(moov, hdlr.start + 8) !== "vide") continue;
    const stsd = child(moov, child(moov, child(moov, mdia, "minf"), "stbl"), "stsd");
    // stsd: version and flags (4), entry count (4), then the first entry's size (4) and format (4).
    if (!stsd || stsd.end - stsd.start < 16) continue;
    const tag = ascii(moov, stsd.start + 12);
    return { codec: TAGS[tag] ?? "other", tag };
  }
  return null;
}

/**
 * The codec of an MP4 or QuickTime video's picture, in a few small reads however
 * big the file: the top-level boxes are skipped over until the index ("moov"),
 * which is read whole. Null for other files (WebM) or a file it cannot make sense of.
 */
export async function sniffVideoCodec(read: ByteReader, size: number): Promise<VideoCodecInfo | null> {
  let at = 0;
  for (let i = 0; i < 64 && at + 8 <= size; i++) {
    const head = await read(at, 16);
    if (head.length < 8) return null;
    if (i === 0 && ascii(head, 4) !== "ftyp") return null;
    let boxSize = u32(head, 0);
    let headLen = 8;
    if (boxSize === 1) {
      if (head.length < 16) return null;
      boxSize = u32(head, 8) * 2 ** 32 + u32(head, 12);
      headLen = 16;
    } else if (boxSize === 0) boxSize = size - at;
    if (boxSize < headLen) return null;
    if (ascii(head, 4) === "moov") {
      const length = Math.min(boxSize, size - at) - headLen;
      if (length > MAX_MOOV_BYTES) return null;
      const moov = new Uint8Array(length);
      let got = 0;
      while (got < length) {
        const part = await read(at + headLen + got, length - got);
        if (part.length === 0) break;
        moov.set(part.subarray(0, length - got), got);
        got += part.length;
      }
      return codecInMoov(moov.subarray(0, got));
    }
    at += boxSize;
  }
  return null;
}

/** A reader over bytes already in memory (tests, a small file). */
export const bytesReader =
  (bytes: Uint8Array): ByteReader =>
  async (offset, length) =>
    bytes.subarray(offset, Math.min(bytes.length, offset + length));
